/**
 * Plans in IndexedDB, with the rules the server used to enforce.
 *
 * Each function is the local twin of one route in the original
 * `backend/app/api/v1/plans.py`, down to its error codes: a stale save is a 409
 * the merge can rebase, an inconsistent document is a 422 that never reaches
 * storage, and the plan and table caps are `quota_exceeded`.
 */

import { LIMITS } from '../config'
import { defaultSeatsFor } from '../events'
import type { LocaleCode } from '../i18n'
import { newId } from '../ids'
import { docOf, validateDoc } from '../merge'
import { planWords } from '../planWords'
import { starterTables } from '../templates'
import { ApiError, type EventKind, type Guest, type Plan, type PlanSummary } from '../types'
import { type StoredPlan, asStorageError, getDb, nowIso } from './db'
import { photoUrlsFor } from './photos'
import { announce } from './tabs'

export const TITLE_MAX = 160

const cleanTitle = (title: string, fallback: string): string =>
  title.trim().slice(0, TITLE_MAX) || fallback

const withoutUrl = (guest: Guest): Guest => ({ ...guest, photoUrl: null })

async function run<T>(work: () => Promise<T>): Promise<T> {
  try {
    return await work()
  } catch (error) {
    throw asStorageError(error)
  }
}

export function summaryOf(record: StoredPlan): PlanSummary {
  let seatCount = 0
  let seatedCount = 0
  for (const table of record.tables) {
    seatCount += table.seats.length
    for (const seat of table.seats) if (seat.guestId) seatedCount += 1
  }
  return {
    id: record.id,
    title: record.title,
    eventKind: record.eventKind,
    eventDate: record.eventDate,
    venue: record.venue,
    language: record.language,
    version: record.version,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    tableCount: record.tables.length,
    guestCount: record.guests.length,
    seatedCount,
    seatCount,
  }
}

/** A stored record as the editor sees it: counts derived, photo URLs minted. */
export async function planFromRecord(record: StoredPlan): Promise<Plan> {
  const photoIds = record.guests.flatMap((guest) => (guest.photoAssetId ? [guest.photoAssetId] : []))
  const urls = photoIds.length > 0 ? await photoUrlsFor(photoIds) : new Map<string, string>()
  return {
    ...summaryOf(record),
    viewport: { ...record.viewport },
    settings: record.settings,
    tables: record.tables,
    guests: record.guests.map((guest) => ({
      ...guest,
      photoUrl: guest.photoAssetId ? (urls.get(guest.photoAssetId) ?? null) : null,
    })),
  }
}

const notFound = () => new ApiError(404, 'not_found', 'That seating plan does not exist.')

const tooManyPlans = () =>
  new ApiError(403, 'quota_exceeded', `A browser can hold at most ${LIMITS.maxPlans} plans.`)

export async function listPlans(): Promise<PlanSummary[]> {
  return run(async () => {
    const db = await getDb()
    const records = await db.getAll('plans')
    return records
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      .map(summaryOf)
  })
}

export async function createPlan(input: {
  /** Blank or missing: the kind's own title, in the plan's language. */
  title?: string
  eventKind?: EventKind
  eventDate?: string | null
  venue?: string | null
  language?: LocaleCode
  template?: 'blank' | 'starter'
}): Promise<Plan> {
  return run(async () => {
    const now = nowIso()
    const kind = input.eventKind ?? 'wedding'
    const language = input.language ?? 'en'
    const record: StoredPlan = {
      id: newId(),
      title: cleanTitle(input.title ?? '', planWords(language).title[kind]),
      eventKind: kind,
      eventDate: input.eventDate ?? null,
      venue: input.venue ?? null,
      language,
      version: 1,
      createdAt: now,
      updatedAt: now,
      viewport: { x: 0, y: 0, zoom: 1 },
      settings: {
        defaultSeats: defaultSeatsFor(kind),
        defaultTableColor: 'blue',
        seatLabels: 'name',
      },
      tables: (input.template ?? 'starter') === 'starter' ? starterTables(kind, language) : [],
      guests: [],
    }

    const db = await getDb()
    const tx = db.transaction('plans', 'readwrite')
    if ((await tx.store.count()) >= LIMITS.maxPlans) throw tooManyPlans()
    await tx.store.add(record)
    await tx.done
    return planFromRecord(record)
  })
}

export async function getPlan(planId: string): Promise<Plan> {
  return run(async () => {
    const db = await getDb()
    const record = await db.get('plans', planId)
    if (!record) throw notFound()
    return planFromRecord(record)
  })
}

/** Raw record, for backups. */
export async function getPlanRecord(planId: string): Promise<StoredPlan | undefined> {
  return run(async () => (await getDb()).get('plans', planId))
}

/**
 * Metadata written outside the document. In practice that is the language: the
 * one thing about a plan the merge does not own. Deliberately does not bump the
 * version, so switching language never makes an open editor's next save stale.
 */
export async function updatePlan(
  planId: string,
  patch: { title?: string; eventDate?: string | null; venue?: string | null; language?: LocaleCode },
): Promise<Plan> {
  return run(async () => {
    const db = await getDb()
    const tx = db.transaction('plans', 'readwrite')
    const record = await tx.store.get(planId)
    if (!record) throw notFound()
    if (patch.title !== undefined) record.title = cleanTitle(patch.title, record.title)
    if (patch.eventDate !== undefined) record.eventDate = patch.eventDate
    if (patch.venue !== undefined) record.venue = patch.venue
    if (patch.language !== undefined) record.language = patch.language
    record.updatedAt = nowIso()
    await tx.store.put(record)
    await tx.done
    return planFromRecord(record)
  })
}

/**
 * Replace the whole document atomically — the port of `PUT /plans/{id}/layout`.
 *
 * Whole-document writes keep autosave idempotent, and the version check makes a
 * second tab's stale save a 409 rather than a silent overwrite. Only the
 * document, the camera and the settings are written: language and timestamps
 * belong to the record.
 */
export async function saveLayout(plan: Plan): Promise<Plan> {
  if (plan.tables.length > LIMITS.maxTablesPerPlan) {
    throw new ApiError(
      403,
      'quota_exceeded',
      `A plan can hold at most ${LIMITS.maxTablesPerPlan} tables.`,
    )
  }
  // A backup refuses more, so storing more would make the next one unrestorable.
  if (plan.guests.length > LIMITS.maxGuestsPerPlan) {
    throw new ApiError(
      403,
      'quota_exceeded',
      `A plan can hold at most ${LIMITS.maxGuestsPerPlan} guests.`,
    )
  }
  const doc = docOf(plan)
  const problems = validateDoc(doc)
  if (problems.length > 0) {
    throw new ApiError(422, 'validation_error', problems.join('; '), problems)
  }

  return run(async () => {
    const db = await getDb()
    const tx = db.transaction('plans', 'readwrite')
    const existing = await tx.store.get(plan.id)
    if (!existing) throw notFound()
    if (existing.version !== plan.version) {
      throw new ApiError(409, 'conflict', 'This plan was changed somewhere else.', {
        currentVersion: existing.version,
        submittedVersion: plan.version,
      })
    }
    const next: StoredPlan = {
      ...existing,
      title: doc.title,
      eventKind: doc.eventKind,
      eventDate: doc.eventDate,
      venue: doc.venue,
      viewport: { x: plan.viewport.x, y: plan.viewport.y, zoom: plan.viewport.zoom },
      settings: doc.settings,
      tables: doc.tables,
      guests: doc.guests.map(withoutUrl),
      version: existing.version + 1,
      updatedAt: nowIso(),
    }
    await tx.store.put(next)
    await tx.done
    announce({ type: 'saved', planId: next.id, version: next.version })
    return planFromRecord(next)
  })
}

export async function deletePlan(planId: string): Promise<void> {
  await run(async () => {
    const db = await getDb()
    await db.delete('plans', planId)
  })
  announce({ type: 'deleted', planId })
}

/**
 * Copy a plan: layout, guests, settings and language. Every table, chair and
 * guest gets a fresh id and seating follows the guests across; photos are
 * shared by asset id, because an asset's bytes never change.
 */
export async function duplicatePlan(planId: string, title?: string): Promise<Plan> {
  return run(async () => {
    const db = await getDb()
    const tx = db.transaction('plans', 'readwrite')
    const source = await tx.store.get(planId)
    if (!source) throw notFound()
    if ((await tx.store.count()) >= LIMITS.maxPlans) throw tooManyPlans()

    const guestIds = new Map<string, string>()
    const guests = source.guests.map((guest) => {
      const id = newId()
      guestIds.set(guest.id, id)
      return { ...guest, id, photoUrl: null }
    })
    const tables = source.tables.map((table) => ({
      ...table,
      id: newId(),
      seats: table.seats.map((seat) => ({
        ...seat,
        id: newId(),
        guestId: seat.guestId ? (guestIds.get(seat.guestId) ?? null) : null,
      })),
    }))
    const now = nowIso()
    const copy: StoredPlan = {
      ...source,
      id: newId(),
      title: cleanTitle(title ?? `${source.title} (copy)`, source.title),
      version: 1,
      createdAt: now,
      updatedAt: now,
      tables,
      guests,
    }
    await tx.store.add(copy)
    await tx.done
    return planFromRecord(copy)
  })
}

export async function planVersion(planId: string): Promise<{ version: number; updatedAt: string }> {
  return run(async () => {
    const db = await getDb()
    const record = await db.get('plans', planId)
    if (!record) throw notFound()
    return { version: record.version, updatedAt: record.updatedAt }
  })
}
